/**
 * File: recent_articles.tsx
 * Description: Public-facing component that displays the most recently updated FAQ articles across all categories,
 *                  with a "load more" button to progressively reveal additional entries.
 */

import { useMemo, useState } from "react"
import {
  Typography,
  useTheme,
  Link,
  Box,
} from "@mui/material";
import { ColorsPalletTheme } from "@/constants/colors_pallet_theme";
import { Link as RouterLink } from 'react-router-dom';
import type { Category, ExtendedArticle } from "@/interfaces/category";
import { flattenCategoryArticles } from "@/utils/flatten_articles";

/**
 * Props for the RecentArticles component.
 *
 * @property {Category[]} categories - Full list of FAQ categories, each containing their articles.
 */
interface RecentArticleListProps {
  categories: Category[];
}

const DEFAULT_ARTICLES_TO_DISPLAY = 5

/**
 * Renders a paginated list of the most recently updated FAQ articles sorted
 * by creation date descending. Each entry links to its parent category and
 * individual article pages. A "Load more" button reveals additional entries
 * in increments of five.
 *
 * @param {Category[]} props.categories - All FAQ categories from which articles are flattened and sorted.
 * @returns {JSX.Element} A list of recent article cards, or a fallback message when no articles exist.
 */
function RecentArticles({ categories }: RecentArticleListProps) {

    const [numArticlesToDisplay, setNumArticlesToDisplay] = useState(DEFAULT_ARTICLES_TO_DISPLAY);

    // Main App Theme
    const theme = useTheme();
    const colors = ColorsPalletTheme(theme.palette.mode);

    /**
     * Increments the number of visible articles by DEFAULT_ARTICLES_TO_DISPLAY.
     */
    function handleArticlesDisplay() {
        setNumArticlesToDisplay(numArticlesToDisplay + DEFAULT_ARTICLES_TO_DISPLAY);
    }

    // Flatten all categories into a single array, newest-first, to check whether
    // any articles exist at all and to render the list.
    const allArticles = useMemo<ExtendedArticle[]>(
        () =>
            flattenCategoryArticles(categories).sort((a, b) =>
                new Date(b.creation_date).getTime() -
                new Date(a.creation_date).getTime()
            ),
        [categories]
    );

    return (
        allArticles.length <= 0 ?
            <Typography variant="h6">No hay Artículos recientes</Typography>
        :
        <Box data-testid='recent-articles-list'>
            { allArticles.slice(0,numArticlesToDisplay).map(article => (
                    <Box sx={{ width: {xs:'95vw' , sm:'40vw'}, display:"flex", flexDirection: 'column', }}>
                        <Link
                            component={RouterLink}
                            to={`/categoria?idcategoria=${String(article.categoryId)}`}
                            sx={{color: colors.gray[100], width: 'fit-content'}}
                            underline="hover"
                        >
                            <Typography variant="h6">{article.categoryTitle}</Typography>
                        </Link>
                        <Link
                            component={RouterLink}
                            to={`/articulo?idcategoria=${String(article.categoryId)}&idarticulo=${String(article.id)}`}
                            sx={{color: colors.gray[100], width: 'fit-content'}}
                            underline="hover"
                        >
                            <Typography variant="body2">{article.title}</Typography>
                        </Link>
                        <Typography variant="body1" sx={{color: colors.gray[100], alignSelf:"flex-end"}}>
                            Última Actualización: {new Date(article.creation_date).toLocaleDateString('es-ES')}
                        </Typography>
                        <Box sx={{ paddingTop: 1, borderBottom: `2px solid ${colors.gray[800]}`, width: "100%" }}/>
                    </Box>
                ))
            }
            { allArticles.length > numArticlesToDisplay ?
                    <Link
                        component="button"
                        underline="always"
                        variant="body2"
                        onClick={() => {
                            handleArticlesDisplay()
                        }}
                        sx={{ width: 'fit-content', paddingTop:2, marginBottom: 2}}
                    >
                        <Typography variant="h6" sx={{
                            textDecoration: "underline",
                            color: colors.blueAccent[500],
                            '&:hover': {
                                color: colors.blueAccent[300]
                            }
                            }}
                        >
                            Más Artículos
                        </Typography>
                    </Link>
                : (<></>)
            }
        </Box>
    );
}

export default RecentArticles;
